import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/authService';

const CreditHistoryWidget = ({ clientId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (clientId) {
      loadHistory();
    }
  }, [clientId]);

  const loadHistory = async () => {
    try {
      setLoading(true);

      const response = await api.get(`/api/credit-policy/credit-history/${clientId}`);
      const data = response.data;
      setHistory(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getProviderLabel = (provider) => {
    const labels = {
      'serasa': 'Serasa',
      'serasa_simulation': 'Serasa (Simulação)',
      'boavista': 'Boa Vista SCPC',
      'boavista_simulation': 'Boa Vista (Simulação)',
      'spc': 'SPC Brasil',
      'spc_simulation': 'SPC (Simulação)',
      'simulation': 'Simulação Interna'
    };
    return labels[provider] || provider;
  };

  const getProviderBadge = (provider) => {
    if (provider.includes('simulation')) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    }
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-blue-600 dark:text-blue-400';
    if (score >= 40) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 20) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (!clientId) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Carregando histórico...
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-3">
          <ClockIcon className="w-6 h-6 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Histórico de Análises
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Nenhuma análise de crédito realizada ainda
        </p>
      </div>
    );
  }

  const displayHistory = expanded ? history : history.slice(0, 3);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ClockIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Histórico de Análises
          </h3>
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {history.length} {history.length === 1 ? 'consulta' : 'consultas'}
        </span>
      </div>

      <div className="space-y-3">
        {displayHistory.map((item, index) => (
          <div
            key={item.id || index}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                {item.success ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                )}
                <span className={`px-2 py-1 rounded text-xs font-medium ${getProviderBadge(item.provider)}`}>
                  {getProviderLabel(item.provider)}
                </span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(item.created_at).toLocaleString('pt-BR')}
              </span>
            </div>

            {item.success && item.score && (
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Score</p>
                  <p className={`text-2xl font-bold ${getScoreColor(item.score)}`}>
                    {item.score}
                  </p>
                </div>
                {item.classification && (
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Classificação</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {item.classification}
                    </p>
                  </div>
                )}
              </div>
            )}

            {item.restrictions && item.restrictions.length > 0 && (
              <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                <p className="text-xs font-semibold text-red-800 dark:text-red-300 mb-1">
                  Restrições Encontradas:
                </p>
                <ul className="text-xs text-red-700 dark:text-red-400 list-disc list-inside">
                  {item.restrictions.map((restriction, idx) => (
                    <li key={idx}>{restriction}</li>
                  ))}
                </ul>
              </div>
            )}

            {!item.success && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                Falha na consulta
              </div>
            )}
          </div>
        ))}
      </div>

      {history.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          {expanded ? 'Mostrar menos' : `Mostrar todas (${history.length})`}
        </button>
      )}
    </div>
  );
};

export default CreditHistoryWidget;
